import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL } from '@/config/env'
import { useAuthStore } from '@/stores/auth-store'
import { refreshSession, SessionChangedError } from '@/lib/session-refresh'
import { dashboardWs } from '@/lib/ws-client'

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean
    _authSessionVersion?: number
  }
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use(
  (config) => {
    const { accessToken, sessionVersion } = useAuthStore.getState().auth
    if (
      config._authSessionVersion !== undefined &&
      config._authSessionVersion !== sessionVersion
    ) {
      throw new SessionChangedError()
    }
    config._authSessionVersion = sessionVersion
    if (accessToken) {
      config.headers = config.headers ?? {}
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error: AxiosError) => Promise.reject(error)
)

apiClient.interceptors.response.use(
  (response) => {
    if (
      response.config._authSessionVersion !==
      useAuthStore.getState().auth.sessionVersion
    ) {
      throw new SessionChangedError()
    }
    return response
  },
  async (error: AxiosError) => {
    const status = error.response?.status
    const originalRequest = error.config as
      | InternalAxiosRequestConfig
      | undefined
    const { accessToken, sessionVersion } = useAuthStore.getState().auth

    // Fence old successes and errors alike: an old 401 must not log out a
    // newly signed-in account through global query error handlers.
    if (
      originalRequest &&
      originalRequest._authSessionVersion !== sessionVersion
    ) {
      return Promise.reject(new SessionChangedError())
    }

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/refresh')
    ) {
      return Promise.reject(error)
    }

    // Every waiter gets one retry, including requests joining a shared refresh.
    originalRequest._retry = true
    let nextAccessToken: string
    if (
      accessToken &&
      originalRequest.headers.Authorization !== `Bearer ${accessToken}`
    ) {
      // Another same-session refresh may have completed after this request
      // went out. Use that token instead of rotating the refresh token again.
      nextAccessToken = accessToken
    } else {
      try {
        nextAccessToken = await refreshSession()
      } catch (refreshError) {
        return Promise.reject(refreshError)
      }
    }

    // A logout or another login after resolution must not replay this request
    // under a different session, or forward its obsolete token to the socket.
    const currentAuth = useAuthStore.getState().auth
    if (
      currentAuth.sessionVersion !== sessionVersion ||
      currentAuth.accessToken !== nextAccessToken
    ) {
      return Promise.reject(new SessionChangedError())
    }
    dashboardWs.sendTokenRefresh(nextAccessToken)
    originalRequest.headers = originalRequest.headers ?? {}
    originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`

    // Retried 401/500 failures are request failures, not refresh failures.
    return apiClient(originalRequest)
  }
)

export { apiClient }
